import { cloneDeep, isEqual } from 'lodash-es'
import { detailedDiff } from 'deep-object-diff'
import { effectScope, onMounted, onScopeDispose, watch } from '@/composition/vue/compositionApi'
import {
  getBulkManager,
  getDomainManagerService,
  getEM,
  getExportService,
  getFilterCompareService,
  getHistoryService,
  getLoggerService,
  getModelSubscriptionService,
  getNewQueryParamsService,
  getNotificationService,
  getPreloaderManager,
  getTableBaseRecoveryService,
  getTableBuilder,
  getTableManager,
  getWrapperManagerService,
} from '@/core/container/ioc'
import {
  COLUMNS_URL_QUERY_PARAM,
  DEFAULT_PER_PAGE,
  FILTERS_URL_QUERY_PARAM,
  PAGING_URL_QUERY_PARAM,
  SEARCH_URL_QUERY_PARAM,
} from '@/core/tables/types'
import { get } from '@/core/container/container'
import type BaseModel from '@/data/models/BaseModel'
import ModelMapper from '@/data/utils/modelMapper'
import type { RepositorySettings } from '@/decorators/repositoryDecorators'
import type { Endpoint } from '@/services/endpoints'
import { useTableService } from '@/composition/container'
import type { BaseTableInterface } from '@/core/tables/baseTableInterface'
import { TmTableError } from '@/core/error/table/tmTableError'
import type { TableKeyBuilder } from '@/core/tables/tableKeyBuilder'
import { createFilters } from '@/composition/filters'
import type { CounterInterface } from '@/services/tables/types'
import { createSorters } from '@/composition/sorters'
import { createGroupers } from '@/composition/groupers'
import { createPaginators } from '@/composition/paginators'
import { createSearchers } from '@/composition/searchers'
import { createColumns } from '@/composition/columns'
import type { QueryChangedPayload } from '@/services/route/types'
import { queryToDotNotation } from '@/utils/router'
import { fromDotNotation } from '@/utils/object/fromDotNotation'
import type { WrapperParams } from '@/services/wrappers/types'
import { createWrapper } from '@/composition/modal'
import type { TmWrappers } from '@/wrappers'
import type { WrapperTableParamsToSave } from '@/services/wrappers/table/types'
import { isPromiseSettled } from '@/utils/promise/isResolved'
import { assertComponentScope } from '@/composition/utils/assertComponentScope'
import type { CreateTableResult, UseTableBuilderSettings } from '@/composition/table/types'

export const defaultTableWrapperParamsToSave: WrapperTableParamsToSave = {
  [PAGING_URL_QUERY_PARAM]: ['perPage'],
  [SEARCH_URL_QUERY_PARAM]: [],
  [FILTERS_URL_QUERY_PARAM]: [],
  [COLUMNS_URL_QUERY_PARAM]: [],
}

const getTableSubscriptions = (
  tableEntityModel: typeof BaseModel,
  extraEvents: string[] = [],
  excludeRelatedModels = false,
) => {
  if (excludeRelatedModels) return [tableEntityModel.entity]
  const tableEvents = ModelMapper.gatherRelatedModels(tableEntityModel).map((m) => m.entity)
  const eventsIntersection = tableEvents.filter((e) => extraEvents.includes(e))
  if (eventsIntersection.length > 0) {
    throw new TmTableError(
      `Subscription events for table entity "${tableEntityModel.entity}" already contains event type ${JSON.stringify(
        eventsIntersection,
      )}`,
    )
  }
  return (tableEvents.length === 0 ? extraEvents : [...tableEvents, ...extraEvents]).filter((v) => v)
}

const toQueryDotNotationCauseDetailedDiffWorkOnlyWithOneLevel = (data: Record<string, unknown>) => {
  let resultQuery = {}
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue
    }
    resultQuery = {
      ...resultQuery,
      ...queryToDotNotation(key, '', value),
    }
  }
  return resultQuery
}

const getTableQueryChangePayload = (
  nextWrapperParams: WrapperParams,
  prevWrapperParams: WrapperParams,
): QueryChangedPayload => {
  return {
    prev: prevWrapperParams,
    next: nextWrapperParams,
    diff: detailedDiff(
      toQueryDotNotationCauseDetailedDiffWorkOnlyWithOneLevel(prevWrapperParams),
      toQueryDotNotationCauseDetailedDiffWorkOnlyWithOneLevel(nextWrapperParams),
    ),
  }
}

export const createTableWithManualLoad = <TEntity extends typeof BaseModel = typeof BaseModel>(
  settings: UseTableBuilderSettings<TEntity>,
): CreateTableResult<TEntity> & { load: () => Promise<void> } => {
  assertComponentScope('createTableWithManualLoad')

  const { promise: tableInit, resolve: resolveTableInit, reject: rejectTableInit } = Promise.withResolvers<void>()
  const { promise: initLoading, resolve: resolveInitLoading, reject: rejectInitLoading } = Promise.withResolvers<void>()

  const em = getEM()
  const tableManager = getTableManager()
  const queryParamsService = getNewQueryParamsService()
  const loggerService = getLoggerService()

  try {
    const existingTable = tableManager.getTable(settings.tableModelId)
    existingTable.cleanUp()
  } catch {
    loggerService.log('table', 'Skipping clean up on create…')
  }
  const wrapperId = tableManager.getWrapperIdByTableId<TmWrappers>(
    (settings.tableWrapperId as TmWrappers) || settings.tableModelId,
  )
  const { wrapperParams, build, destroy } = createWrapper(wrapperId, settings.wrapperType || 'lsTable')
  build()
  const repoSettings = em.getRepository(settings.entity).settings() as RepositorySettings<Endpoint>

  if (!repoSettings.fetch && !settings.gridService) {
    throw new TmTableError(
      'To use table you should specify `fetch` params in repository.settings() or provide custom gridService',
    )
  }

  const endpoint = settings.schemaEndpoint ?? repoSettings.schemaEndpoint ?? repoSettings.fetch!

  const columnManager = createColumns(settings)
  const columns = columnManager.addServiceForTable(settings.tableModelId)

  const searcherManager = createSearchers({ ...settings, endpoint })
  searcherManager.addServiceForTable(settings.tableModelId, {
    supportedSearchFields: settings.supportedSearchFilters,
  })

  const filterServiceManager = createFilters({ ...settings, endpoint })

  const sorterManager = createSorters({ ...settings, endpoint })
  sorterManager.addServiceForTable(settings.tableModelId, {
    defaultColumns: columns.instance.getDefaultColumns(),
    defaultSorter: settings.defaultSorter,
    entityName: settings.sortEntityName || settings.entity.entity,
  })

  const grouperManager = createGroupers({ ...settings, endpoint })
  grouperManager.addServiceForTable(settings.tableModelId)

  const paginatorManager = createPaginators(settings)
  paginatorManager.addServiceForTable(
    settings.tableModelId,
    settings.paginationSettings ?? { perPage: DEFAULT_PER_PAGE },
  )

  settings.subscriptions = settings.subscriptions || []
  const table: BaseTableInterface<TEntity> = getTableBuilder().build({
    subscriptions: getTableSubscriptions(
      settings.entity,
      Array.isArray(settings.subscriptions) ? settings.subscriptions : [settings.subscriptions],
      settings.excludeRelatedModels,
    ),
    bulkDeleteEndpoint: repoSettings.bulkDeleteEndpoint,
    tableModelId: settings.tableModelId,
    tableWrapperId: wrapperId,
    filterServiceManager,
    tableExpandService: settings.tableExpandService,
    sorterManager,
    grouperManager,
    paginatorManager,
    searcherManager,
    columnManager,
    em,
    domainManager: getDomainManagerService(),
    export: settings.exportService ? get(settings.exportService) : getExportService(),
    logger: loggerService,
    bulk: settings.bulkManager ?? getBulkManager(),
    preloaderManager: getPreloaderManager(),
    filterCompareService: getFilterCompareService(),
    notification: getNotificationService(),
    history: getHistoryService(),
    subscription: getModelSubscriptionService(),
    entity: settings.entity,
    gridService: settings.gridService,
    tableWrapperService: getWrapperManagerService(),
    tableConstructor: settings.tableConstructor,
    keyBuilder: get<TableKeyBuilder>('TableKeyBuilder'),
    counter: settings.counter ? get<CounterInterface>(settings.counter) : settings.counter,
    facetSettings: settings.facetSettings,
    alignment: settings.alignment,
    customFiltersDefaultState: settings.customFiltersDefaultState,
    tableWrapperParamsToSave: settings.tableWrapperParamsToSave || defaultTableWrapperParamsToSave,
    canBeRestored: settings.canBeRestored ?? true,
    recoveryService: settings.recoveryService || getTableBaseRecoveryService(),
    fastInsert: settings.fastInsert ?? false,
    noSelectAll: settings.noSelectAll ?? false,
    allowSelectAllWithNonDefaultFilters: settings.allowSelectAllWithNonDefaultFilters ?? false,
    shouldResetOnCreateOrDelete: settings.shouldResetOnCreateOrDelete ?? true,
    preserveSelectionOnQueryChange: settings.preserveSelectionOnQueryChange ?? true,
    applyFiltersOnExport: settings.applyFiltersOnExport ?? false,
    variablePagination: settings.variablePagination ?? false,
  })

  const { id } = useTableService(settings.tableModelId, table)
  tableManager.addTable(table)

  onMounted(() => {
    if (!table.getFilters().length) {
      table.initFilter()
    }
  })

  let isDisposed = false
  onScopeDispose(async () => {
    isDisposed = true
    loggerService.log('table', 'Unsubscribe table', table.getTableModelId())
    watchScope.stop()
    queryParamsService.unsubscribe(wrapperId)
    table.destroy()
    destroy()
  })

  // Snapshot wrapper params after init but before onAfterInit, so we can detect if
  // onAfterInit changed them (e.g. applied route-based filters via setParamsByTableData).
  // If it did, we re-sync internal table state before the first fetch.
  // If onAfterInit updates internal state directly (e.g. table.populateFiltersFromUrl),
  // wrapper params stay unchanged and we skip re-sync to avoid overwriting those changes.
  let wrapperParamsAfterInit: WrapperParams
  let wrapperParamsAtLoadStart: WrapperParams | undefined
  settings.onBeforeInit?.(table)
  table
    .init()
    .then(() => {
      wrapperParamsAfterInit = cloneDeep(wrapperParams.value)
      settings.onAfterInit?.(table)
      resolveTableInit()
    })
    .catch(() => {
      rejectTableInit()
    })

  const load = async () => {
    await tableInit

    // The wrapper is destroyed on dispose, touching it again recreates it with a non-table type
    if (!(await isPromiseSettled(initLoading)) && !isDisposed) {
      if (!isEqual(wrapperParamsAfterInit, wrapperParams.value)) {
        table.resyncTableParams()
      }
      settings.onBeforeLoad?.(table)
      wrapperParamsAtLoadStart = cloneDeep(wrapperParams.value)

      try {
        await table.load()
        resolveInitLoading()
      } catch (e) {
        getNotificationService().contactSupport(e as Error)
        rejectInitLoading(e)
      }
    }
  }

  initLoading.then(() => {
    if (settings.selectedRows) {
      table.selectRows(settings.selectedRows)
    }

    const wrapperManager = getWrapperManagerService()
    if (settings.wrapperType && settings.wrapperType === 'queryTable') {
      queryParamsService.subscribe(wrapperId, (route) => {
        loggerService.log('table', JSON.stringify(route.query), 'onChange')
        const newQuery = cloneDeep(route.query)
        const params: Record<string, unknown> = {}
        wrapperManager.getAllTableParamsKeys(wrapperId).forEach((key) => {
          const k = wrapperManager.getTableParamKey(key, wrapperId)
          if (newQuery[k]) {
            params[key] = fromDotNotation(JSON.parse(newQuery[k] as string))
          }
        })
        wrapperManager.setParams(wrapperId, params)
      })
    }
  })

  // The point of control a table. Any changes in wrapper start re-fetch of data for a table. Actually, no different
  // what type of wrapper uses for a table "table" or "queryTable". Each table for "queryTable" uses data from state first.
  //
  // effectScope is detached — we control its lifecycle manually via onScopeDispose.
  // watch is registered after initLoading to avoid redundant fetches during init/load.
  const watchScope = effectScope(true)
  initLoading.then(() => {
    // Guard against early unmount: if the component is destroyed before initLoading resolves,
    // onScopeDispose has already called watchScope.stop(), so we skip registering the watcher.
    if (!watchScope.active) {
      return
    }

    watchScope.run(() => {
      watch(wrapperParams, (next, prev) => {
        table.on(getTableQueryChangePayload(next, prev))
      })
    })

    // The watcher starts only after the initial load.
    // If user changed params during the load (e.g. page switch), watcher won't react on it;
    // Call table.on() once with that diff to handle this case
    const current = wrapperParams.value
    if (wrapperParamsAtLoadStart && !isEqual(wrapperParamsAtLoadStart, current)) {
      table.on(getTableQueryChangePayload(current, wrapperParamsAtLoadStart))
    }
  })

  return { id, table, initLoading, load }
}

export const createTable = <TEntity extends typeof BaseModel = typeof BaseModel>(
  settings: UseTableBuilderSettings<TEntity>,
): CreateTableResult<TEntity> => {
  const { load, ...rest } = createTableWithManualLoad(settings)
  load()
  return rest
}
